import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, shadowTokenStore } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Skip the /auth/me check while a Google OAuth callback is being processed
    // (GoogleAuthCallback owns auth state in that window) — otherwise this can
    // race the session exchange and redirect to /login before it finishes.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    if (!tokenStore.get()) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      tokenStore.clear();
      shadowTokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) {
      return { mfaRequired: true, mfaToken: data.mfa_token, methods: data.methods };
    }
    tokenStore.set(data.access_token);
    shadowTokenStore.clear();
    setUser(data.user);
    return { mfaRequired: false, user: data.user };
  }, []);

  // Called once the second factor (TOTP code or Passkey) has been verified —
  // mirrors login()'s success path so requires_password_change / mfa_setup
  // flags on the returned user are handled identically either way.
  const completeMfaLogin = useCallback((accessToken, userData) => {
    tokenStore.set(accessToken);
    shadowTokenStore.clear();
    setUser(userData);
  }, []);

  // Used by GoogleAuthCallback once it has exchanged session_id for our own
  // app JWT — mirrors `login()` so the rest of the app behaves identically.
  const loginWithToken = useCallback((accessToken, userData) => {
    tokenStore.set(accessToken);
    shadowTokenStore.clear();
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    tokenStore.clear();
    shadowTokenStore.clear();
    setUser(null);
    window.location.href = "/login";
  }, []);

  const startMasquerade = useCallback(async (targetUserId) => {
    const currentToken = tokenStore.get();
    const { data } = await api.post(`/admin/masquerade/${targetUserId}`);
    // Keep the real admin's token as a shadow so we can restore it later even
    // if the impersonation JWT expires mid-session.
    if (currentToken) shadowTokenStore.set(currentToken);
    tokenStore.set(data.access_token);
    setUser(data.user);
    window.location.href = "/";
    return data.user;
  }, []);

  const stopMasquerade = useCallback(async () => {
    try {
      const { data } = await api.post("/admin/masquerade/stop");
      tokenStore.set(data.access_token);
      shadowTokenStore.clear();
      setUser(data.user);
    } catch {
      // Fall back to restoring the shadow token if backend rejects (e.g. expired imp token).
      const shadow = shadowTokenStore.get();
      if (shadow) {
        tokenStore.set(shadow);
        shadowTokenStore.clear();
      }
    }
    window.location.href = "/settings/super-admin";
  }, []);

  const isMasquerading = !!user?.impersonated_by;

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, completeMfaLogin, logout, refresh, startMasquerade, stopMasquerade, isMasquerading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
